import type {
  AdminAsset,
  AdminJournalSection,
  UpdateJournalPostRequest,
  VersionedJournalPost,
} from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { SaveState } from '../../components/SaveState.js';
import { useDianaUIPortal } from '../../ui/provider/DianaUIProvider.js';
import { ConflictDialog } from '../../components/ConflictDialog.js';
import { ConfirmedSlugField } from '../../components/ConfirmedSlugField.js';
import { SortableList } from '../../components/SortableList.js';
import { plainToRichText } from '../../components/RichTextField.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { Button, Checkbox } from '../../ui/index.js';
import { ContextualGalleryField, ContextualMediaField } from '../media/ContextualMediaField.js';
import { SelectField, TextAreaField, TextField } from '../settings/SettingsEditor.js';
import { RichTextEditor } from './RichTextEditor.js';
import './JournalEditor.css';

type JournalDraft = Omit<UpdateJournalPostRequest, 'expectedRevision'>;

export function JournalEditor({
  post,
  assets,
  save,
}: {
  post: VersionedJournalPost;
  assets: readonly AdminAsset[];
  save(request: UpdateJournalPostRequest): Promise<VersionedJournalPost>;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const portal = useDianaUIPortal();
  const conflictRoot = useRef<HTMLDivElement>(null);
  const autosave = useAutosave<JournalDraft>({
    initialValue: journalDraft(post),
    initialRevision: post.revision,
    save: async ({ value, expectedRevision }) => {
      const result = await save({ ...value, expectedRevision });
      return { value: journalDraft(result), revision: result.revision };
    },
    readConflict: (error) =>
      readRevisionConflict(error, (current) => (isJournal(current) ? journalDraft(current) : null)),
  });
  const draft = autosave.snapshot.value;
  useEditorCommandRegistration({
    status: autosave.snapshot.status,
    save: () => void autosave.flush(),
  });
  const update = (patch: Partial<JournalDraft>) => autosave.edit({ ...draft, ...patch });
  const updatePayload = (patch: Partial<JournalDraft['payload']>) =>
    update({ payload: { ...draft.payload, ...patch } });
  const updateSection = (changed: AdminJournalSection) =>
    update({
      sections: draft.sections.map((section) => (section.id === changed.id ? changed : section)),
    });

  const conflicted = autosave.snapshot.status === 'Conflict';
  useEffect(() => {
    if (!conflicted) return;
    setSettingsOpen(false);
    // Let the drawer release its modal focus trap before focusing conflict actions.
    const frame = requestAnimationFrame(() =>
      conflictRoot.current?.querySelector('button')?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [conflicted]);
  const saveStatus = (
    <>
      <SaveState status={autosave.snapshot.status} />
      {autosave.snapshot.status === 'Error' ? (
        <button
          className="button button--quiet"
          type="button"
          onClick={() => void autosave.retry()}
        >
          Retry save
        </button>
      ) : null}
    </>
  );

  return (
    <article className="journal-editor">
      <h1 className="sr-only">{draft.payload.title || 'Journal article'}</h1>
      <Dialog.Root open={settingsOpen && !conflicted} onOpenChange={setSettingsOpen}>
        <header className="journal-editor__header">
          <Link className="journal-editor__back" to="/journal" aria-label="Back to Journal">
            <ArrowLeft size={20} aria-hidden="true" />
            <span>Journal</span>
          </Link>
          <div className="journal-editor__status">{saveStatus}</div>
          <Dialog.Trigger className="journal-editor__settings" aria-label="Post settings">
            <SlidersHorizontal size={19} aria-hidden="true" />
            <span>Post settings</span>
          </Dialog.Trigger>
        </header>
        <div className="journal-editor__content">
          <div className="journal-editor__cover">
            <ContextualMediaField
              title="Article cover"
              profile="wide"
              cropProfile="landscape-16-9"
              description="Used in the Journal index and at the top of this article."
              value={draft.payload.cover}
              assets={assets}
              onChange={(cover) => updatePayload({ cover })}
            />
          </div>
          <div className="journal-editor__metadata">
            <span>{draft.payload.category || 'Category not set'}</span>
            <span>{draft.payload.dateLabel || 'Date not set'}</span>
          </div>
          <GrowingTextArea
            label="Article title"
            className="journal-editor__title"
            value={draft.payload.title}
            onChange={(title) => updatePayload({ title })}
          />
          <GrowingTextArea
            label="Article excerpt"
            className="journal-editor__excerpt"
            value={draft.payload.excerpt}
            onChange={(excerpt) => updatePayload({ excerpt })}
          />
          <section className="journal-editor__sections" aria-label="Article sections">
            <SortableList
              items={draft.sections}
              label={(section) => (section.type === 'RichText' ? 'Text' : section.type)}
              onChange={(sections) => update({ sections })}
              render={(section) => (
                <JournalSectionEditor
                  section={section}
                  assets={assets}
                  onChange={updateSection}
                  onRemove={() =>
                    update({
                      sections: normalize(draft.sections.filter((item) => item.id !== section.id)),
                    })
                  }
                />
              )}
            />
            <div className="journal-editor__add">
              <button
                className="button button--quiet"
                type="button"
                onClick={() => addSection('RichText')}
              >
                Add text
              </button>
              <button
                className="button button--quiet"
                type="button"
                disabled={!assets[0]}
                onClick={() => addSection('Image')}
              >
                Add image
              </button>
              <button
                className="button button--quiet"
                type="button"
                disabled={!assets[0]}
                onClick={() => addSection('Gallery')}
              >
                Add gallery
              </button>
            </div>
          </section>
          <footer className="journal-editor__preview">
            <Link className="button button--quiet" to="/preview">
              Open Preview workspace
            </Link>
            <p>Preview uses this saved structured article without raw HTML or embedded scripts.</p>
          </footer>
        </div>
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="journal-settings__overlay" />
          <Dialog.Content
            className="journal-settings"
            onCloseAutoFocus={(event) => {
              if (conflicted) event.preventDefault();
            }}
          >
            <header className="journal-settings__header">
              <div>
                <p>{draft.payload.title}</p>
                <Dialog.Title>Post settings</Dialog.Title>
              </div>
              <Dialog.Close className="journal-settings__close" aria-label="Close post settings">
                <X size={20} aria-hidden="true" />
              </Dialog.Close>
            </header>
            <Dialog.Description className="sr-only">
              Article details, website access and search appearance. Changes save automatically.
            </Dialog.Description>
            <div className="journal-settings__body">
              <section className="journal-settings__group" aria-label="Article details">
                <h3>Article details</h3>
                <TextField
                  label="Article category"
                  value={draft.payload.category}
                  onChange={(category) => updatePayload({ category })}
                />
                <TextField
                  label="Article date"
                  value={draft.payload.dateLabel}
                  onChange={(dateLabel) => updatePayload({ dateLabel })}
                />
                <TextField
                  label="Publication date"
                  type="date"
                  optional
                  value={draft.payload.publishedAt?.slice(0, 10) ?? ''}
                  onChange={(date) =>
                    updatePayload({
                      publishedAt: date
                        ? new Date(`${date}T12:00:00.000Z`).toISOString()
                        : undefined,
                    })
                  }
                />
                <ConfirmedSlugField
                  label="Article slug"
                  slug={draft.slug}
                  routeFor={computedRoute}
                  onConfirm={(slug) => update({ slug })}
                />
                <p className="journal-settings__note">
                  Canonical route is computed as <code>{computedRoute(draft.slug)}</code>.
                </p>
              </section>
              <section className="journal-settings__group" aria-label="Website visibility">
                <h3>Website access</h3>
                <SelectField
                  label="Website access"
                  value={draft.siteAccess}
                  options={['AdminOnly', 'Public']}
                  optionLabels={{ AdminOnly: 'Admin only', Public: 'Public' }}
                  onChange={(siteAccess) =>
                    update({ siteAccess: siteAccess as JournalDraft['siteAccess'] })
                  }
                />
                <p className="journal-settings__note">
                  Public articles appear on the website after publishing from the global Publish
                  control.
                </p>
              </section>
              <section className="journal-settings__group" aria-label="Search appearance">
                <h3>In Google</h3>
                <TextField
                  label="SEO title"
                  value={draft.payload.seo.title}
                  onChange={(title) => updatePayload({ seo: { ...draft.payload.seo, title } })}
                />
                <TextAreaField
                  label="SEO description"
                  value={draft.payload.seo.description}
                  onChange={(description) =>
                    updatePayload({ seo: { ...draft.payload.seo, description } })
                  }
                />
                <Checkbox
                  label="Prevent search indexing"
                  description="A public article stays on the website but is excluded from search and the sitemap."
                  checked={draft.payload.seo.noindex}
                  onChange={(event) =>
                    updatePayload({
                      seo: { ...draft.payload.seo, noindex: event.currentTarget.checked },
                    })
                  }
                />
              </section>
              <ContextualMediaField
                title="Social sharing image"
                profile="wide"
                cropProfile="social"
                description="Optional override for Open Graph and social previews."
                value={draft.payload.seo.socialImage}
                assets={assets}
                onChange={(socialImage) =>
                  updatePayload({ seo: { ...draft.payload.seo, socialImage } })
                }
              />
            </div>
            <footer className="journal-settings__footer">
              <div className="journal-settings__save">{saveStatus}</div>
              <Dialog.Close asChild>
                <Button variant="primary">Done</Button>
              </Dialog.Close>
              <p>Saved automatically · goes live when you publish</p>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <div ref={conflictRoot}>
        <ConflictDialog
          open={conflicted}
          onAcceptServer={autosave.acceptServerVersion}
          onReapplyLocal={autosave.reapplyLocal}
          onCopyLocal={() =>
            void navigator.clipboard?.writeText(JSON.stringify(autosave.copyLocal(), null, 2))
          }
        />
      </div>
    </article>
  );

  function addSection(type: AdminJournalSection['type']) {
    const base = {
      id: crypto.randomUUID(),
      position: draft.sections.length,
    };
    const section: AdminJournalSection =
      type === 'RichText'
        ? { ...base, type, payload: { body: plainToRichText('New article paragraph.') } }
        : type === 'Image'
          ? {
              ...base,
              type,
              payload: { media: { assetId: assets[0]!.id } },
            }
          : {
              ...base,
              type,
              payload: {
                layout: 'EditorialGrid',
                items: [{ media: { assetId: assets[0]!.id } }],
              },
            };
    update({ sections: [...draft.sections, section] });
  }
}

function GrowingTextArea({
  label,
  value,
  className,
  onChange,
}: {
  label: string;
  value: string;
  className: string;
  onChange(value: string): void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) return;
    const resize = () => {
      field.style.height = 'auto';
      field.style.height = `${field.scrollHeight}px`;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(field);
    return () => observer.disconnect();
  }, [value]);
  return (
    <textarea
      ref={ref}
      className={className}
      aria-label={label}
      rows={1}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

function JournalSectionEditor({
  section,
  assets,
  onChange,
  onRemove,
}: {
  section: AdminJournalSection;
  assets: readonly AdminAsset[];
  onChange(section: AdminJournalSection): void;
  onRemove(): void;
}) {
  return (
    <section className="section-editor">
      <header>
        <div>
          <strong>{section.type === 'RichText' ? 'Text' : section.type}</strong>
        </div>
        <button className="text-action text-action--danger" type="button" onClick={onRemove}>
          Remove section
        </button>
      </header>
      {section.type === 'RichText' ? (
        <RichTextEditor
          label="Article body"
          value={section.payload.body}
          onChange={(body) => onChange({ ...section, payload: { ...section.payload, body } })}
        />
      ) : null}
      {section.type === 'Image' ? (
        <div className="block-fields">
          <ContextualMediaField
            title="Article image"
            profile="standard"
            cropProfile="landscape-3-2"
            description="Upload here or choose an existing photograph from Media."
            value={section.payload.media}
            assets={assets}
            onChange={(media) => onChange({ ...section, payload: { ...section.payload, media } })}
          />
          <TextField
            label="Image caption"
            value={section.payload.caption ?? ''}
            optional
            onChange={(caption) =>
              onChange({
                ...section,
                payload: { ...section.payload, caption: caption || undefined },
              })
            }
          />
        </div>
      ) : null}
      {section.type === 'Gallery' ? (
        <div className="block-fields">
          <SelectField
            label="Article gallery layout"
            value={section.payload.layout}
            options={['EditorialGrid', 'Filmstrip', 'Masonry']}
            onChange={(layout) =>
              onChange({
                ...section,
                payload: { ...section.payload, layout: layout as typeof section.payload.layout },
              })
            }
          />
          <ContextualGalleryField
            title="Article gallery"
            profile="standard"
            description="Upload photographs, refine alt text and arrange the reading order."
            items={section.payload.items}
            assets={assets}
            createItem={(media) => ({ media })}
            onChange={(items) => onChange({ ...section, payload: { ...section.payload, items } })}
          />
        </div>
      ) : null}
    </section>
  );
}

function journalDraft(post: VersionedJournalPost): JournalDraft {
  return {
    slug: post.slug,
    siteAccess: post.siteAccess,
    payload: post.payload,
    sections: post.sections,
  };
}

function computedRoute(slug: string) {
  return `/journal/${slug || 'new-article'}`;
}

function normalize(sections: AdminJournalSection[]) {
  return sections.map((section, position) => ({ ...section, position }));
}

function isJournal(value: unknown): value is VersionedJournalPost {
  return !!value && typeof value === 'object' && 'sections' in value && 'route' in value;
}
